import { LOCALSTORAGE_USER_CACHE_PREFIX } from "constants/index";
import { Button } from "components/Graphs/Button";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import {
  clearPostLoginRedirect,
  DEFAULT_POST_LOGIN_REDIRECT,
} from "utils/redirects";
import { FullScreenLoadingSpinner } from "components/FullScreenLoader";
import InvitationAcceptedConfirmation from "components/InvitationAcceptedConfirmation";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { invitationClient } from "server/devplane/Invitations";

const BROKEN_INVITE_LINK_ERROR =
  "The invite link is broken. Please ask for a new invitation and try again.";
const INVITE_ACCEPT_ERROR =
  "We were unable to add you to this organization. Please ask for a new invitation and try again.";
const CACHED_INVITE_TOKEN_KEY = `${LOCALSTORAGE_USER_CACHE_PREFIX}-invite-token`;

function getInviteSearchParams(search: string): {
  inviteOrgId: string | null;
  inviteToken: string | null;
} {
  const searchParams = new URLSearchParams(search);

  return {
    inviteOrgId: searchParams.get("orgID"),
    inviteToken: searchParams.get("token"),
  };
}

async function acceptOrganizationInvite(token: string): Promise<void> {
  await invitationClient.acceptInviteFromLink({ token });
}

const Invite: React.FC = () => {
  const history = useHistory();
  const { search } = useLocation();
  const { refreshOrgAccesses, refreshOrgs, setActiveOrgId } =
    useContext(OrgContext);
  const { refreshMe } = useContext(UserContext);
  const { clear: clearEnvironments } = useContext(WorkspaceContext);
  const inviteRequestStartedRef = useRef(false);
  const { inviteOrgId, inviteToken } = useMemo(
    () => getInviteSearchParams(search),
    [search],
  );
  const [joinedOrganization, setJoinedOrganization] = useState<{
    name?: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const inviteLinkError =
    !inviteToken || !inviteOrgId ? BROKEN_INVITE_LINK_ERROR : "";
  const displayErrorMessage = errorMessage || inviteLinkError;

  useEffect(() => {
    if (
      !inviteOrgId ||
      !inviteToken ||
      inviteRequestStartedRef.current ||
      errorMessage
    ) {
      return;
    }

    inviteRequestStartedRef.current = true;
    clearPostLoginRedirect();

    acceptOrganizationInvite(inviteToken)
      .then(async () => {
        window.localStorage.removeItem(CACHED_INVITE_TOKEN_KEY);
        const [organizations] = await Promise.all([
          refreshOrgs(),
          refreshMe(),
          refreshOrgAccesses(),
        ]);
        clearEnvironments();
        setActiveOrgId(inviteOrgId);
        setJoinedOrganization({
          name: organizations.find(
            (organization) => organization.id === inviteOrgId,
          )?.name,
        });
      })
      .catch((error) => {
        console.error("Failed to accept invite:", error);
        inviteRequestStartedRef.current = false;
        setErrorMessage(INVITE_ACCEPT_ERROR);
      });
  }, [
    errorMessage,
    clearEnvironments,
    history,
    inviteOrgId,
    inviteToken,
    refreshOrgAccesses,
    refreshMe,
    refreshOrgs,
    setActiveOrgId,
  ]);

  const goHome = useCallback(() => {
    clearPostLoginRedirect();
    window.localStorage.removeItem(CACHED_INVITE_TOKEN_KEY);
    history.replace(DEFAULT_POST_LOGIN_REDIRECT);
  }, [history]);

  const goToInvitedOrg = useCallback(() => {
    if (inviteOrgId) {
      history.replace(`/org/${inviteOrgId}/environments`);
    }
  }, [history, inviteOrgId]);

  if (displayErrorMessage) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <div className="max-w-lg text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Could not join organization
          </h1>
          <p className="mt-3 text-base text-gray-600 dark:text-secondary">
            {displayErrorMessage}
          </p>
          <Button className="mt-6" variant="primary" onClick={goHome}>
            Go home
          </Button>
        </div>
      </div>
    );
  }

  if (joinedOrganization) {
    return (
      <InvitationAcceptedConfirmation
        organizationName={joinedOrganization.name}
        onContinue={goToInvitedOrg}
      />
    );
  }

  return (
    <FullScreenLoadingSpinner
      description="Accepting invitation..."
      heightClassName="min-h-[calc(100vh-48px)]"
      spinnerClassName="text-white"
    />
  );
};

export default Invite;
